import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { MetaResponse } from '@/common/types/metaResponse'
import { InfiniteData, keepPreviousData, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query'
import { PrivateCompanyLeadData } from '../types/privateCompanyLead'

type usePrivateCompanyLeadsInfiniteProps<T> = {
  filters?: {
    search?: string
    state?: string[]
    creatorId?: string
    showOnlyArchived?: boolean
  }
  options?: Partial<UseInfiniteQueryOptions<any, any, InfiniteData<any>>>
  mapper?: (data: any) => T
}

export const usePrivateCompanyLeadsInfinite = <T,>({
  filters: { search = '', state = [], creatorId, showOnlyArchived } = {},
  options,
  mapper,
}: usePrivateCompanyLeadsInfiniteProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useInfiniteQuery({
    queryKey: ['privateCompanyLeads', search, state, creatorId, showOnlyArchived],
    queryFn: ({ pageParam }) => {
      const params = {
        search,
        'q[state_eq]': state,
        'q[creator_id_eq]': creatorId,
        'q[archived_at_not_null]': showOnlyArchived ? true : undefined,
        page: pageParam,
      }

      return privateAxios.get('/funnel/private_company_leads', {
        params,
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[]; meta: MetaResponse } }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    placeholderData: keepPreviousData,
    ...options,
    select: (data) => {
      const flatData = data?.pages?.reduce((acc, page) => {
        return [...acc, ...page?.data?.data]
      }, [] as PrivateCompanyLeadData[])

      return {
        data: mapper ? mapper(flatData) : (flatData as T),
        meta: data?.pages[data?.pages?.length - 1]?.data?.meta as MetaResponse,
      }
    },
  })

  return { data, meta, ...rest }
}

export default usePrivateCompanyLeadsInfinite
